import { useQuery } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import type {
  CrmDashboardSnapshot,
  CrmDashboardHeatmapResponse,
  CrmDashboardHeatmapSubpipelineMeta,
  DashboardConversionPeriod,
  CrmDashboardConversionRow,
  CrmDashboardStageMovementSummary,
} from "@shared/api";

const EMPTY_STAGE_MOVEMENT_SUMMARY: CrmDashboardStageMovementSummary = {
  totalPositiveMoves: 0,
  totalNegativeMoves: 0,
  avgPositiveMovesPerDay: 0,
  positiveMovesOnCallDays: 0,
  negativeMovesOnCallDays: 0,
  daysInRange: 0,
  daysWithTeamCalls: 0,
  positiveOnCallDaysSharePct: null,
  newDealsInFilterPeriod: 0,
  callerSplits: [],
};

export type DashboardQueryFilters = {
  pipelineId: string;
  dateFrom: string;
  dateTo: string;
};

/** Rolling 30 calendar days inclusive in UTC (matches dashboard backend when no filter). */
export function defaultDashboardDateRangeUtc(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  const mo = now.getUTCMonth();
  const d = now.getUTCDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (dt: Date) =>
    `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
  const end = new Date(Date.UTC(y, mo, d));
  const start = new Date(Date.UTC(y, mo, d - 29));
  return { dateFrom: fmt(start), dateTo: fmt(end) };
}

export function getDefaultDashboardFilters(): DashboardQueryFilters {
  const { dateFrom, dateTo } = defaultDashboardDateRangeUtc();
  return {
    pipelineId: "",
    dateFrom,
    dateTo,
  };
}

function monthLocaleFromLang(lang: string): string {
  if (lang === "sk") return "sk-SK";
  if (lang === "hu") return "hu-HU";
  return "en-GB";
}

async function fetchDashboard(locale: string, f: DashboardQueryFilters): Promise<CrmDashboardSnapshot> {
  const q = new URLSearchParams();
  q.set("monthLocale", monthLocaleFromLang(locale));
  if (f.pipelineId) q.set("pipelineId", f.pipelineId);
  if (f.dateFrom) q.set("dateFrom", f.dateFrom);
  if (f.dateTo) q.set("dateTo", f.dateTo);
  const url = apiUrl(`/api/crm/dashboard?${q}`);
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) {
    const t = await r.text().catch(() => "");
    throw new Error(t || `dashboard_${r.status}`);
  }
  return r.json() as Promise<CrmDashboardSnapshot>;
}

async function fetchDashboardHeatmap(f: DashboardQueryFilters): Promise<CrmDashboardHeatmapResponse> {
  const q = new URLSearchParams();
  if (f.pipelineId) q.set("pipelineId", f.pipelineId);
  if (f.dateFrom) q.set("dateFrom", f.dateFrom);
  if (f.dateTo) q.set("dateTo", f.dateTo);
  const qs = q.toString();
  const url = apiUrl(`/api/crm/dashboard/heatmap${qs ? `?${qs}` : ""}`);
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) {
    const t = await r.text().catch(() => "");
    throw new Error(t || `dashboard_heatmap_${r.status}`);
  }
  return r.json() as Promise<CrmDashboardHeatmapResponse>;
}

export function dashboardQueryKey(locale: string, f: DashboardQueryFilters) {
  return ["crm-dashboard", locale, f.pipelineId, f.dateFrom, f.dateTo] as const;
}

export function dashboardHeatmapQueryKey(f: DashboardQueryFilters) {
  return ["crm-dashboard-heatmap", f.pipelineId, f.dateFrom, f.dateTo] as const;
}

export function useDashboardHeatmap(f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardHeatmapQueryKey(f),
    queryFn: () => fetchDashboardHeatmap(f),
    staleTime: 120_000,
  });
}

export function useDashboardSubpipelines(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d): CrmDashboardHeatmapSubpipelineMeta[] => d.subpipelines ?? [],
  });
}

export function useActiveDeals(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => d.activeDeals,
  });
}

export function usePipelineStages(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => d.pipelineStages,
  });
}

export function useMonthlyTrend(locale: string, f: DashboardQueryFilters, months = 6) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => {
      const rows = d.monthlyTrend;
      if (f.dateFrom && f.dateTo) return rows;
      return rows.slice(-months);
    },
  });
}

export function useLeadSources(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => d.leadSources,
  });
}

export function useConversionRate(locale: string, f: DashboardQueryFilters, period: DashboardConversionPeriod) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d): CrmDashboardConversionRow[] => {
      if (period === "week") return d.conversionWeek;
      if (period === "quarter") return d.conversionQuarter;
      return d.conversionMonth;
    },
  });
}

export function useDealKpis90d(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => d.kpis90d,
  });
}

export function useDealsCreatedDaily(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => d.dealsCreatedDaily ?? [],
  });
}

export function useDealStageMovementDaily(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => d.dealStageMovementDaily ?? [],
  });
}

/** Chart rows + side-panel summary; shares the same dashboard query as other hooks. */
export function useDealStageMovementPanel(locale: string, f: DashboardQueryFilters) {
  return useQuery({
    queryKey: dashboardQueryKey(locale, f),
    queryFn: () => fetchDashboard(locale, f),
    staleTime: 60_000,
    select: (d) => ({
      rows: d.dealStageMovementDaily ?? [],
      summary: d.stageMovementSummary ?? EMPTY_STAGE_MOVEMENT_SUMMARY,
    }),
  });
}
